import { getDataspaceId } from "./config/runtimeConfig";
import { SEALED_SECRET_PREFIX, sealConnectionCredential } from "./dataConnectionClient";
import {
  addStringNoLocale,
  addUrl,
  createContainerAt,
  createAclFromFallbackAcl,
  getSolidDatasetWithAcl,
  getResourceAcl,
  hasResourceAcl,
  hasAccessibleAcl,
  saveAclFor,
  setAgentResourceAccess,
  setAgentDefaultAccess,
  createSolidDataset,
  createThing,
  deleteFile,
  getDatetime,
  getSolidDataset,
  getContainedResourceUrlAll,
  getStringNoLocale,
  getStringNoLocaleAll,
  getThing,
  getThingAll,
  getUrl,
  getUrlAll,
  removeAll,
  removeThing,
  saveSolidDatasetAt,
  setDatetime,
  setStringNoLocale,
  setThing,
  setUrl,
} from "@inrupt/solid-client";
import { getPodRoot } from "./solidCatalog";
import {
  decryptCredentialSecret,
} from "./solidCredentialToken";

const SDM_NS = "https://w3id.org/solid-dataspace-manager#";
const SDM_DATA_CONNECTION_TYPE = `${SDM_NS}DataConnection`;
const SDM_HOME_ASSISTANT_TYPE = `${SDM_NS}HomeAssistantConnection`;
const SDM_HOME_ASSISTANT_ENTITY_COLLECTION_TYPE = `${SDM_NS}HomeAssistantEntityCollection`;
const SDM_HOME_ASSISTANT_ENTITY_TYPE = `${SDM_NS}HomeAssistantEntity`;
const SDM_CUSTOM_CONNECTION_TYPE = `${SDM_NS}CustomConnection`;
const SDM_CONNECTION_ID = `${SDM_NS}connectionId`;
const SDM_CONNECTION_NAME = `${SDM_NS}connectionName`;
const SDM_CONNECTION_DESCRIPTION = `${SDM_NS}connectionDescription`;
const SDM_CONNECTION_URL = `${SDM_NS}connectionUrl`;
const SDM_CONNECTION_PORT = `${SDM_NS}connectionPort`;
const SDM_CONNECTION_ENDPOINT = `${SDM_NS}connectionEndpoint`;
const SDM_CONNECTION_STORAGE_DOCUMENT = `${SDM_NS}connectionStorageDocument`;
const SDM_CONNECTION_DATA_MODEL = `${SDM_NS}connectionDataModel`;
const SDM_CONNECTION_SYNC_INTERVAL_MINUTES = `${SDM_NS}syncIntervalMinutes`;
const SDM_CONNECTION_ACCESS_TOKEN = `${SDM_NS}encryptedAccessToken`;
const SDM_CONNECTION_TOKEN_ALGORITHM = `${SDM_NS}tokenEncryptionAlgorithm`;
const SDM_SELECTED_ENTITY = `${SDM_NS}selectedEntity`;
const SDM_CONNECTION_METHOD = `${SDM_NS}connectionMethod`;
const SDM_CONNECTION_AUTH_TYPE = `${SDM_NS}connectionAuthType`;
const SDM_CONNECTION_AUTH_SECRET = `${SDM_NS}encryptedAuthSecret`;
const SDM_CONNECTION_AUTH_HEADER = `${SDM_NS}connectionAuthHeader`;
const SDM_CONNECTION_HEADERS = `${SDM_NS}connectionHeadersJson`;
const SDM_ENTITY_ID = `${SDM_NS}entityId`;
const SDM_ENTITY_DOMAIN = `${SDM_NS}entityDomain`;
const SDM_ENTITY_NAME = `${SDM_NS}entityName`;
const SDM_ENTITY_STATE = `${SDM_NS}entityState`;
const SDM_ENTITY_UNIT = `${SDM_NS}entityUnit`;
const SDM_ENTITY_DEVICE_CLASS = `${SDM_NS}entityDeviceClass`;
const SDM_ENTITY_ATTRIBUTES = `${SDM_NS}entityAttributesJson`;
const SDM_OBSERVED_AT = `${SDM_NS}observedAt`;
const SDM_LAST_CHANGED = `${SDM_NS}lastChanged`;
const SDM_LAST_UPDATED = `${SDM_NS}lastUpdated`;
const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const DCTERMS_CREATED = "http://purl.org/dc/terms/created";
const DCTERMS_MODIFIED = "http://purl.org/dc/terms/modified";
const RDFS_LABEL = "http://www.w3.org/2000/01/rdf-schema#label";
const SOSA_NS = "http://www.w3.org/ns/sosa/";
const SOSA_OBSERVATION = `${SOSA_NS}Observation`;
const SOSA_OBSERVABLE_PROPERTY = `${SOSA_NS}ObservableProperty`;
const SOSA_OBSERVED_PROPERTY = `${SOSA_NS}observedProperty`;
const SOSA_HAS_SIMPLE_RESULT = `${SOSA_NS}hasSimpleResult`;
const SOSA_RESULT_TIME = `${SOSA_NS}resultTime`;
const HOME_ASSISTANT_SECRET_NAME = "homeassistant";
const HOME_ASSISTANT_LEGACY_ID = "homeassistant";
const CUSTOM_SECRET_PREFIX = "custom-connection";
const DEMO_ENCRYPTION_ALGORITHM = "demo-xor-v1";
const SDM_ENTITY_SNAPSHOT_DATA_MODEL = "sdm-entity-snapshot";
const SOLID_SYNC_JSON_DATA_MODEL = "solid-sync-json";
const DEFAULT_HOME_ASSISTANT_DATA_MODEL = SOLID_SYNC_JSON_DATA_MODEL;

export const HOME_ASSISTANT_DATA_MODELS = [
  {
    id: SDM_ENTITY_SNAPSHOT_DATA_MODEL,
    label: "SDM Entity Snapshot",
  },
  {
    id: "sosa-observation",
    label: "SOSA Observation",
  },
  {
    id: SOLID_SYNC_JSON_DATA_MODEL,
    label: "Solid Sync JSON",
  },
];

export const getConnectionsContainerUrl = (webId) =>
  webId ? `${getPodRoot(webId)}connections/${getDataspaceId() === "test" ? "test/" : ""}` : "";

export const getHomeAssistantConnectionDocumentUrl = (
  webId,
  id = HOME_ASSISTANT_LEGACY_ID
) => {
  if (!webId) return "";
  const normalizedId = normalizeHomeAssistantConnectionId(id);
  const filename =
    normalizedId === HOME_ASSISTANT_LEGACY_ID
      ? "homeassistant.ttl"
      : `homeassistant-${normalizedId}.ttl`;
  return `${getConnectionsContainerUrl(webId)}${filename}`;
};

export const getHomeAssistantEntitiesDocumentUrl = (
  webId,
  id = HOME_ASSISTANT_LEGACY_ID
) => {
  if (!webId) return "";
  const normalizedId = normalizeHomeAssistantConnectionId(id);
  const filename =
    normalizedId === HOME_ASSISTANT_LEGACY_ID
      ? "homeassistant-entities.ttl"
      : `homeassistant-entities-${normalizedId}.ttl`;
  return `${getConnectionsContainerUrl(webId)}${filename}`;
};

export const getHomeAssistantSolidSyncDocumentUrl = (
  webId,
  id = HOME_ASSISTANT_LEGACY_ID
) => {
  if (!webId) return "";
  const normalizedId = normalizeHomeAssistantConnectionId(id);
  const filename =
    normalizedId === HOME_ASSISTANT_LEGACY_ID
      ? "homeassistant-solid-sync.json"
      : `homeassistant-solid-sync-${normalizedId}.json`;
  return `${getConnectionsContainerUrl(webId)}${filename}`;
};

const getCustomConnectionDocumentUrl = (webId, id) =>
  webId && id ? `${getConnectionsContainerUrl(webId)}custom-${id}.ttl` : "";

export const getCustomSyncDocumentUrl = (webId, id) =>
  webId && id
    ? `${getConnectionsContainerUrl(webId)}custom-${slugifyConnectionId(id)}-sync.json`
    : "";

const getHomeAssistantConnectionThingUrl = (
  webId,
  id = HOME_ASSISTANT_LEGACY_ID
) => {
  const documentUrl = getHomeAssistantConnectionDocumentUrl(webId, id);
  return documentUrl ? `${documentUrl}#it` : "";
};

const getHomeAssistantEntityCollectionThingUrlFromDocument = (documentUrl) =>
  documentUrl ? `${documentUrl}#it` : "";

const getHomeAssistantEntityThingUrl = (documentUrl, entityId) => {
  return documentUrl && entityId
    ? `${documentUrl}#${encodeURIComponent(entityId)}`
    : "";
};

const getHomeAssistantObservablePropertyThingUrl = (documentUrl, entityId) => {
  return documentUrl && entityId
    ? `${documentUrl}#property-${encodeURIComponent(entityId)}`
    : "";
};

const getCustomConnectionThingUrl = (webId, id) => {
  const documentUrl = getCustomConnectionDocumentUrl(webId, id);
  return documentUrl ? `${documentUrl}#it` : "";
};

export const connectionContainerExists = async (webId, fetch) => {
  const containerUrl = getConnectionsContainerUrl(webId);
  if (!containerUrl || !fetch) return false;

  try {
    await getSolidDataset(containerUrl, { fetch });
    return true;
  } catch (error) {
    if ((error?.statusCode || error?.response?.status) !== 404) throw error;
    return false;
  }
};

export const ensureConnectionsContainer = async (webId, fetch) => {
  const containerUrl = getConnectionsContainerUrl(webId);
  if (!containerUrl || !fetch) return "";

  try {
    await getSolidDataset(containerUrl, { fetch });
    return containerUrl;
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status !== 404) {
      throw err;
    }
  }

  try {
    // Test is a separate child container; create its parent if necessary.
    if (getDataspaceId() === "test") {
      const parent = `${getPodRoot(webId)}connections/`;
      try { await getSolidDataset(parent, { fetch }); }
      catch (error) { if ((error.statusCode || error.response?.status) !== 404) throw error; await createContainerAt(parent, { fetch }); }
    }
    await createContainerAt(containerUrl, { fetch });
    const resource = await getSolidDatasetWithAcl(containerUrl, { fetch });
    let acl = hasResourceAcl(resource) ? getResourceAcl(resource) : hasAccessibleAcl(resource) ? createAclFromFallbackAcl(resource) : null;
    if (!acl) throw new Error("Private connection storage requires ACL access.");
    for (const thing of getThingAll(acl)) acl = removeThing(acl, thing);
    const owner = { read: true, append: true, write: true, control: true };
    acl = setAgentResourceAccess(acl, webId, owner);
    acl = setAgentDefaultAccess(acl, webId, owner);
    await saveAclFor(resource, acl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status !== 409 && status !== 412) {
      throw err;
    }
  }

  return containerUrl;
};

const hasProtocol = (value) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

const looksLikeRawIpv6Address = (value) => {
  const trimmed = String(value || "").trim();
  return (
    trimmed.includes(":") &&
    trimmed.indexOf(":") !== trimmed.lastIndexOf(":") &&
    !trimmed.startsWith("[") &&
    !trimmed.includes("/") &&
    !hasProtocol(trimmed)
  );
};

const prepareUrl = (value, defaultProtocol = "http") => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  if (hasProtocol(trimmed)) return trimmed;
  if (looksLikeRawIpv6Address(trimmed)) return `${defaultProtocol}://[${trimmed}]`;
  return `${defaultProtocol}://${trimmed}`;
};

const normalizeUrl = (value, defaultProtocol = "http") => {
  const withProtocol = prepareUrl(value, defaultProtocol);
  if (!withProtocol) return "";
  const parsed = new URL(withProtocol);
  const pathname = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
  return `${parsed.protocol}//${parsed.host}${pathname}`;
};

const normalizeSourceEndpointPath = (value, fallback = "/") => {
  const raw = String(value || "").trim();
  const fallbackValue = String(fallback || "/").trim() || "/";
  const source = raw || fallbackValue;

  try {
    const parsed = hasProtocol(source)
      ? new URL(source)
      : new URL(source.startsWith("/") ? source : `/${source}`, "http://local");
    const pathname = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "/";
    return `${pathname}${parsed.search || ""}`;
  } catch {
    throw new Error("Enter a valid source endpoint.");
  }
};

const normalizePort = (value) => {
  const trimmed = String(value || "").trim();
  const port = Number(trimmed);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    throw new Error("Enter a valid port between 1 and 65535.");
  }
  return String(port);
};

const buildEndpoint = (url, port) => {
  const parsed = new URL(url);
  parsed.port = port;
  const pathname = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
  return `${parsed.origin}${pathname}`;
};

const buildCustomEndpoint = (url, port, sourceEndpoint) => {
  const parsed = new URL(url);
  parsed.port = port;
  const endpointPath = normalizeSourceEndpointPath(sourceEndpoint);
  const endpoint = new URL(endpointPath, parsed.origin);
  return `${parsed.origin}${endpoint.pathname}${endpoint.search || ""}`;
};

const getDefaultPortForParsedUrl = (parsedUrl) =>
  parsedUrl.port || (parsedUrl.protocol === "https:" ? "443" : "80");

const inferPortFromUrl = (value) => {
  try {
    return getDefaultPortForParsedUrl(new URL(prepareUrl(value)));
  } catch {
    return "";
  }
};

const normalizeDataModel = (value) => {
  const dataModel = String(value || DEFAULT_HOME_ASSISTANT_DATA_MODEL).trim();
  return HOME_ASSISTANT_DATA_MODELS.some((model) => model.id === dataModel)
    ? dataModel
    : DEFAULT_HOME_ASSISTANT_DATA_MODEL;
};

export const isSolidSyncJsonDataModel = (value) =>
  normalizeDataModel(value) === SOLID_SYNC_JSON_DATA_MODEL;

const normalizeSyncIntervalMinutes = (value) => {
  const trimmed = String(value ?? "0").trim();
  if (!trimmed) return "0";
  const interval = Number(trimmed);
  if (!Number.isInteger(interval) || interval < 0 || interval > 1440) {
    throw new Error("Enter a sync interval between 0 and 1440 minutes.");
  }
  return String(interval);
};

const normalizeStorageDocumentUrl = (webId, value, { allowRdf = false } = {}) => {
  const raw = String(value || "").trim();
  if (!raw) return getHomeAssistantEntitiesDocumentUrl(webId);

  try {
    const parsed = hasProtocol(raw)
      ? new URL(raw)
      : new URL(raw.replace(/^\/+/, ""), getPodRoot(webId));
    const base = getConnectionsContainerUrl(webId);
    const extension = allowRdf ? ".ttl" : ".json";
    if (parsed.username || parsed.password || parsed.search || parsed.hash || !parsed.href.startsWith(base) || !parsed.pathname.endsWith(extension))
      throw new Error("Choose a file matching the data model in this Pod's connection area.");
    return parsed.href;
  } catch {
    throw new Error("Enter a valid Pod storage document URL.");
  }
};

const normalizeCustomStorageDocumentUrl = (webId, value, id) => {
  const raw = String(value || "").trim();
  if (!raw) return getCustomSyncDocumentUrl(webId, id);

  try {
    const parsed = hasProtocol(raw)
      ? new URL(raw)
      : new URL(raw.replace(/^\/+/, ""), getPodRoot(webId));
    const base = getConnectionsContainerUrl(webId);
    if (parsed.username || parsed.password || parsed.search || parsed.hash || !parsed.href.startsWith(base) || !parsed.pathname.endsWith(".json"))
      throw new Error("Choose a JSON file in this Pod's connection area.");
    return parsed.href;
  } catch {
    throw new Error("Enter a valid Pod storage document URL.");
  }
};

const getContainerUrlForDocument = (documentUrl) => {
  const parsed = new URL(documentUrl);
  parsed.hash = "";
  parsed.search = "";
  parsed.pathname = parsed.pathname.replace(/[^/]*$/, "");
  return parsed.href;
};

const ensureContainerAtUrl = async (containerUrl, fetch) => {
  if (!containerUrl || !fetch) return "";

  try {
    await getSolidDataset(containerUrl, { fetch });
    return containerUrl;
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status !== 404) {
      throw err;
    }
  }

  const parentUrl = getContainerUrlForDocument(containerUrl.replace(/\/$/, ""));
  if (parentUrl && parentUrl !== containerUrl) {
    try {
      await getSolidDataset(parentUrl, { fetch });
    } catch (err) {
      const status = err?.statusCode || err?.response?.status;
      if (status === 404) {
        await ensureContainerAtUrl(parentUrl, fetch);
      } else {
        throw err;
      }
    }
  }

  try {
    await createContainerAt(containerUrl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status !== 409 && status !== 412) {
      throw err;
    }
  }

  return containerUrl;
};

const normalizeSelectedEntities = (selectedEntities = []) =>
  Array.from(
    new Set(
      selectedEntities
        .map((entityId) => String(entityId || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

const slugifyConnectionId = (value) =>
  String(value || "custom")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "custom";

const createConnectionId = (name) =>
  `${slugifyConnectionId(name)}-${Date.now().toString(36)}`;

const normalizeHomeAssistantConnectionId = (id) =>
  slugifyConnectionId(id || HOME_ASSISTANT_LEGACY_ID);

const getHomeAssistantSecretName = (id) => {
  const normalizedId = normalizeHomeAssistantConnectionId(id);
  return normalizedId === HOME_ASSISTANT_LEGACY_ID
    ? HOME_ASSISTANT_SECRET_NAME
    : `${HOME_ASSISTANT_SECRET_NAME}-${normalizedId}`;
};

const getHomeAssistantConnectionIdFromDocumentUrl = (documentUrl) => {
  const filename = String(documentUrl || "").split("/").pop() || "";
  if (filename === "homeassistant.ttl") return HOME_ASSISTANT_LEGACY_ID;
  const match = filename.match(/^homeassistant-(.+)\.ttl$/);
  return match ? normalizeHomeAssistantConnectionId(match[1]) : "";
};

const isHomeAssistantConnectionDocumentUrl = (documentUrl) => {
  const filename = String(documentUrl || "").split("/").pop() || "";
  if (filename === "homeassistant.ttl") return true;
  if (filename === "homeassistant-entities.ttl") return false;
  if (!/^homeassistant-.+\.ttl$/.test(filename)) return false;
  return !filename.startsWith("homeassistant-entities-");
};

const normalizeHttpMethod = (value) => {
  const method = String(value || "GET").trim().toUpperCase();
  return ["GET", "POST", "PUT", "PATCH", "DELETE"].includes(method)
    ? method
    : "GET";
};

const normalizeAuthType = (value) => {
  const authType = String(value || "none").trim().toLowerCase();
  return ["none", "bearer", "api-key", "basic"].includes(authType) ? authType : "none";
};

const normalizeHeadersJson = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
      throw new Error("Additional headers must be a JSON object.");
    }
    return JSON.stringify(parsed, null, 2);
  } catch (err) {
    throw new Error(err?.message || "Additional headers must be valid JSON.");
  }
};

const setOptionalDatetime = (thing, predicate, value) => {
  if (!value) return thing;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return thing;
  return setDatetime(thing, predicate, date);
};

const stringifyAttributes = (attributes) => {
  try {
    return JSON.stringify(attributes || {});
  } catch {
    return "{}";
  }
};

const normalizeMeasurementKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");

const defaultMeasurementKey = (entityId) => {
  const value = String(entityId || "");
  const dotIndex = value.indexOf(".");
  const objectId = dotIndex >= 0 ? value.slice(dotIndex + 1) : value;
  return normalizeMeasurementKey(objectId) || "value";
};

const getUniqueMeasurementKey = (entityId, usedKeys) => {
  const baseKey = defaultMeasurementKey(entityId);
  let key = baseKey;
  let index = 2;
  while (usedKeys.has(key)) {
    key = `${baseKey}_${index}`;
    index += 1;
  }
  usedKeys.add(key);
  return key;
};

const normalizeSolidSyncEntry = (entry) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const capturedAt = String(entry.captured_at || "").trim();
  const measurements = entry.measurements;
  if (!capturedAt || !measurements || typeof measurements !== "object") {
    return null;
  }
  return {
    captured_at: capturedAt,
    measurements,
  };
};

const extractSolidSyncEntries = (existing) => {
  if (!existing) return [];

  if (Array.isArray(existing)) {
    return existing.map(normalizeSolidSyncEntry).filter(Boolean);
  }

  if (typeof existing === "object") {
    if (Array.isArray(existing.entries)) {
      return existing.entries.map(normalizeSolidSyncEntry).filter(Boolean);
    }

    const legacyEntry = normalizeSolidSyncEntry(existing);
    if (legacyEntry) return [legacyEntry];
  }

  throw new Error("Existing Solid Sync resource must be JSON with entries.");
};

const parseSyncDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const newestDate = (dates) => {
  const validDates = dates.filter(Boolean);
  if (!validDates.length) return null;
  return new Date(Math.max(...validDates.map((date) => date.getTime())));
};

const extractJsonSyncDate = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const candidates = [
    parseSyncDate(payload.updated_at),
    parseSyncDate(payload.updatedAt),
    parseSyncDate(payload.captured_at),
    parseSyncDate(payload.capturedAt),
  ];

  if (Array.isArray(payload.entries)) {
    payload.entries.forEach((entry) => {
      candidates.push(parseSyncDate(entry?.captured_at));
      candidates.push(parseSyncDate(entry?.capturedAt));
      candidates.push(parseSyncDate(entry?.updated_at));
      candidates.push(parseSyncDate(entry?.updatedAt));
      candidates.push(parseSyncDate(entry?.observed_at));
      candidates.push(parseSyncDate(entry?.observedAt));
    });
  }

  return newestDate(candidates);
};

export async function loadConnectionLastSync(storageDocumentUrl, fetch) {
  if (!storageDocumentUrl || !fetch) return null;

  const documentUrl = String(storageDocumentUrl || "").trim();
  if (!documentUrl) return null;

  if (documentUrl.toLowerCase().endsWith(".json")) {
    const response = await fetch(documentUrl, {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
    });

    if (response.status === 404 || response.status === 204) return null;
    if (!response.ok) {
      throw new Error(`Sync document could not be loaded (${response.status}).`);
    }

    const body = await response.text();
    if (!body.trim()) return null;
    return extractJsonSyncDate(JSON.parse(body));
  }

  try {
    const dataset = await getSolidDataset(documentUrl, { fetch });
    return newestDate(
      getThingAll(dataset).map((thing) => getDatetime(thing, DCTERMS_MODIFIED))
    );
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

const getResourcePathForDocument = (webId, documentUrl) => {
  const podRoot = getPodRoot(webId);
  if (podRoot && documentUrl.startsWith(podRoot)) {
    return documentUrl.slice(podRoot.length).replace(/^\/+/, "");
  }

  try {
    return new URL(documentUrl).pathname.replace(/^\/+/, "");
  } catch {
    return documentUrl;
  }
};

export const normalizeHomeAssistantConnection = ({
  id,
  webId,
  name,
  url,
  port,
  accessToken,
  encryptedAccessToken = "",
  options = {},
  storageDocumentUrl,
  dataModel,
  syncIntervalMinutes,
  selectedEntities = [],
}) => {
  let parsedUrl;
  const rawUrl = (url || "").trim();
  if (!rawUrl) {
    throw new Error("URL is required.");
  }

  try {
    parsedUrl = new URL(prepareUrl(rawUrl));
  } catch {
    throw new Error("Enter a valid URL.");
  }

  const normalizedPort = normalizePort(port || parsedUrl.port || "8123");
  const inferredProtocol =
    !hasProtocol(rawUrl) && normalizedPort === "443" ? "https" : "http";
  const normalizedUrl = normalizeUrl(rawUrl, inferredProtocol);
  const normalizedAccessToken = String(accessToken || "").trim();
  if (!normalizedAccessToken && !encryptedAccessToken.startsWith(SEALED_SECRET_PREFIX)) {
    throw new Error("Access token is required.");
  }

  const normalizedId = id
    ? normalizeHomeAssistantConnectionId(id)
    : createConnectionId("homeassistant");
  const normalizedName = String(name || "").trim() || "Home Assistant";
  const normalizedDataModel = normalizeDataModel(dataModel);
  const defaultStorageDocumentUrl =
    normalizedDataModel === SOLID_SYNC_JSON_DATA_MODEL
      ? getHomeAssistantSolidSyncDocumentUrl(webId, normalizedId)
      : getHomeAssistantEntitiesDocumentUrl(webId, normalizedId);

  return {
    id: normalizedId,
    name: normalizedName,
    url: normalizedUrl,
    port: normalizedPort,
    endpoint: buildEndpoint(normalizedUrl, normalizedPort),
    storageDocumentUrl: normalizeStorageDocumentUrl(
      webId,
      storageDocumentUrl || defaultStorageDocumentUrl,
      { allowRdf: normalizedDataModel !== SOLID_SYNC_JSON_DATA_MODEL }
    ),
    dataModel: normalizedDataModel,
    syncIntervalMinutes: normalizeSyncIntervalMinutes(syncIntervalMinutes),
    accessToken: normalizedAccessToken,
    encryptedAccessToken, options,
    selectedEntities: normalizeSelectedEntities(selectedEntities),
  };
};

export const normalizeCustomConnection = ({
  id,
  webId,
  name,
  description,
  url,
  port,
  sourceEndpoint,
  method = "GET",
  authType = "none",
  authSecret = "",
  encryptedAuthSecret = "",
  options = {},
  authHeader = "",
  headersJson = "",
  storageDocumentUrl = "",
}) => {
  const normalizedName = String(name || "").trim();
  if (!normalizedName) {
    throw new Error("Connection name is required.");
  }

  const rawUrl = String(url || "").trim();
  if (!rawUrl) {
    throw new Error("URL is required.");
  }

  let parsedUrl;
  let normalizedUrl;
  try {
    parsedUrl = new URL(prepareUrl(rawUrl));
  } catch {
    throw new Error("Enter a valid URL.");
  }

  const defaultPort =
    parsedUrl.port || (!hasProtocol(rawUrl) ? "443" : getDefaultPortForParsedUrl(parsedUrl));
  const normalizedPort = normalizePort(port || defaultPort);
  const inferredProtocol =
    !hasProtocol(rawUrl) && normalizedPort === "443" ? "https" : "http";
  try {
    normalizedUrl = normalizeUrl(rawUrl, inferredProtocol);
  } catch {
    throw new Error("Enter a valid URL.");
  }
  const normalizedSourceEndpoint = normalizeSourceEndpointPath(
    sourceEndpoint,
    `${parsedUrl.pathname && parsedUrl.pathname !== "/" ? parsedUrl.pathname : "/"}${parsedUrl.search || ""}`
  );
  const baseUrl = new URL(normalizedUrl);
  baseUrl.pathname = "";
  baseUrl.search = "";
  baseUrl.hash = "";
  normalizedUrl = `${baseUrl.protocol}//${baseUrl.host}`;
  const normalizedEndpoint = buildCustomEndpoint(
    normalizedUrl,
    normalizedPort,
    normalizedSourceEndpoint
  );

  const normalizedAuthType = normalizeAuthType(authType);
  const normalizedAuthSecret = String(authSecret || "").trim();
  const normalizedAuthHeader =
    normalizedAuthType === "api-key"
      ? String(authHeader || "X-API-Key").trim()
      : "";

  if (normalizedAuthType !== "none" && !normalizedAuthSecret && !encryptedAuthSecret.startsWith(SEALED_SECRET_PREFIX)) {
    throw new Error("Authentication secret is required.");
  }

  if (normalizedAuthType === "api-key" && !normalizedAuthHeader) {
    throw new Error("API key header name is required.");
  }

  const normalizedHeadersJson = normalizeHeadersJson(headersJson);
  const normalizedId = id ? slugifyConnectionId(id) : createConnectionId(normalizedName);

  return {
    id: normalizedId,
    name: normalizedName,
    description: String(description || "").trim(),
    url: normalizedUrl,
    port: normalizedPort,
    sourceEndpoint: normalizedSourceEndpoint,
    endpoint: normalizedEndpoint,
    method: normalizeHttpMethod(method),
    authType: normalizedAuthType,
    authSecret: normalizedAuthSecret,
    encryptedAuthSecret, options,
    authHeader: normalizedAuthHeader,
    headersJson: normalizedHeadersJson,
    storageDocumentUrl: normalizeCustomStorageDocumentUrl(
      webId,
      storageDocumentUrl,
      normalizedId
    ),
  };
};

const homeAssistantConnectionFromThing = (thing, webId, documentUrl) => {
  if (!thing) return null;
  const types = getUrlAll(thing, RDF_TYPE);
  if (!types.includes(SDM_HOME_ASSISTANT_TYPE)) return null;

  const id =
    getStringNoLocale(thing, SDM_CONNECTION_ID) ||
    getHomeAssistantConnectionIdFromDocumentUrl(documentUrl);
  if (!id) return null;

  const url = getUrl(thing, SDM_CONNECTION_URL) || "";
  const port = getStringNoLocale(thing, SDM_CONNECTION_PORT) || "";
  if (!url || !port) return null;

  const dataModel = normalizeDataModel(
    getStringNoLocale(thing, SDM_CONNECTION_DATA_MODEL)
  );
  const storageDocumentUrl =
    getUrl(thing, SDM_CONNECTION_STORAGE_DOCUMENT) ||
    (dataModel === SOLID_SYNC_JSON_DATA_MODEL
      ? getHomeAssistantSolidSyncDocumentUrl(webId, id)
      : getHomeAssistantEntitiesDocumentUrl(webId, id));
  const encryptedAccessToken =
    getStringNoLocale(thing, SDM_CONNECTION_ACCESS_TOKEN) || "";
  const selectedEntities = getStringNoLocaleAll(thing, SDM_SELECTED_ENTITY) || [];

  return {
    id,
    kind: "home-assistant",
    name: getStringNoLocale(thing, SDM_CONNECTION_NAME) || "Home Assistant",
    url,
    port,
    endpoint: getUrl(thing, SDM_CONNECTION_ENDPOINT) || "",
    storageDocumentUrl,
    dataModel,
    syncIntervalMinutes: normalizeSyncIntervalMinutes(
      getStringNoLocale(thing, SDM_CONNECTION_SYNC_INTERVAL_MINUTES)
    ),
    accessToken: encryptedAccessToken && !encryptedAccessToken.startsWith(SEALED_SECRET_PREFIX)
      ? decryptCredentialSecret(
          encryptedAccessToken,
          webId,
          getHomeAssistantSecretName(id)
        )
      : "",
    encryptedAccessToken,
    tokenAlgorithm:
      getStringNoLocale(thing, SDM_CONNECTION_TOKEN_ALGORITHM) ||
      DEMO_ENCRYPTION_ALGORITHM,
    selectedEntities: normalizeSelectedEntities(selectedEntities),
    options: JSON.parse(getStringNoLocale(thing, `${SDM_NS}connectionOptionsJson`) || "{}"),
    documentUrl,
    createdAt: getDatetime(thing, DCTERMS_CREATED),
    modifiedAt: getDatetime(thing, DCTERMS_MODIFIED),
  };
};

export async function loadHomeAssistantConnection(
  webId,
  fetch,
  id = HOME_ASSISTANT_LEGACY_ID
) {
  const normalizedId = normalizeHomeAssistantConnectionId(id);
  const documentUrl = getHomeAssistantConnectionDocumentUrl(webId, normalizedId);
  const thingUrl = getHomeAssistantConnectionThingUrl(webId, normalizedId);
  if (!documentUrl || !thingUrl || !fetch) return null;

  try {
    const dataset = await getSolidDataset(documentUrl, { fetch });
    const connectionThing = getThing(dataset, thingUrl) || getThingAll(dataset)[0];
    return homeAssistantConnectionFromThing(connectionThing, webId, documentUrl);
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function loadHomeAssistantConnections(webId, fetch) {
  const containerUrl = getConnectionsContainerUrl(webId);
  if (!containerUrl || !fetch) return [];

  let containerDataset;
  try {
    containerDataset = await getSolidDataset(containerUrl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) return [];
    throw err;
  }

  const resourceUrls = getContainedResourceUrlAll(containerDataset).filter(
    (url) => !url.endsWith("/") && isHomeAssistantConnectionDocumentUrl(url)
  );
  const connections = [];

  for (const resourceUrl of resourceUrls) {
    try {
      const dataset = await getSolidDataset(resourceUrl, { fetch });
      const thing = getThing(dataset, `${resourceUrl}#it`) || getThingAll(dataset)[0];
      const connection = homeAssistantConnectionFromThing(thing, webId, resourceUrl);
      if (connection) connections.push(connection);
    } catch {
      // Ignore malformed Home Assistant connection documents.
    }
  }

  return connections.sort((a, b) => {
    const left = a.modifiedAt?.getTime?.() || a.createdAt?.getTime?.() || 0;
    const right = b.modifiedAt?.getTime?.() || b.createdAt?.getTime?.() || 0;
    return right - left || a.id.localeCompare(b.id);
  });
}

export async function saveHomeAssistantConnection(webId, fetch, values) {
  if (!webId || !fetch) return null;

  const normalized = normalizeHomeAssistantConnection({ ...values, webId });
  await ensureConnectionsContainer(webId, fetch);

  const documentUrl = getHomeAssistantConnectionDocumentUrl(webId, normalized.id);
  const thingUrl = getHomeAssistantConnectionThingUrl(webId, normalized.id);
  let dataset;

  try {
    dataset = await getSolidDataset(documentUrl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) {
      dataset = createSolidDataset();
    } else {
      throw err;
    }
  }

  let connectionThing = getThing(dataset, thingUrl) || createThing({ url: thingUrl });
  connectionThing = removeAll(connectionThing, RDF_TYPE);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_ID);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_NAME);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_URL);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_PORT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_ENDPOINT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_STORAGE_DOCUMENT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_DATA_MODEL);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_SYNC_INTERVAL_MINUTES);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_ACCESS_TOKEN);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_TOKEN_ALGORITHM);
  connectionThing = removeAll(connectionThing, SDM_SELECTED_ENTITY);
  connectionThing = removeAll(connectionThing, DCTERMS_MODIFIED);

  connectionThing = addUrl(connectionThing, RDF_TYPE, SDM_DATA_CONNECTION_TYPE);
  connectionThing = addUrl(connectionThing, RDF_TYPE, SDM_HOME_ASSISTANT_TYPE);
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_ID,
    normalized.id
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_NAME,
    normalized.name
  );
  connectionThing = setUrl(connectionThing, SDM_CONNECTION_URL, normalized.url);
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_PORT,
    normalized.port
  );
  connectionThing = setUrl(
    connectionThing,
    SDM_CONNECTION_ENDPOINT,
    normalized.endpoint
  );
  connectionThing = setUrl(
    connectionThing,
    SDM_CONNECTION_STORAGE_DOCUMENT,
    normalized.storageDocumentUrl
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_DATA_MODEL,
    normalized.dataModel
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_SYNC_INTERVAL_MINUTES,
    normalized.syncIntervalMinutes
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_ACCESS_TOKEN,
    await sealConnectionCredential(webId, fetch, getHomeAssistantSecretName(normalized.id), normalized.accessToken, normalized.encryptedAccessToken)
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_TOKEN_ALGORITHM,
    "worker-aes-gcm-v1"
  );
  normalized.selectedEntities.forEach((entityId) => {
    connectionThing = addStringNoLocale(
      connectionThing,
      SDM_SELECTED_ENTITY,
      entityId
    );
  });

  if (!getDatetime(connectionThing, DCTERMS_CREATED)) {
    connectionThing = setDatetime(connectionThing, DCTERMS_CREATED, new Date());
  }
  connectionThing = setDatetime(connectionThing, DCTERMS_MODIFIED, new Date());

  connectionThing = setStringNoLocale(connectionThing, `${SDM_NS}connectionOptionsJson`, JSON.stringify(normalized.options || {}));
  dataset = setThing(dataset, connectionThing);
  await saveSolidDatasetAt(documentUrl, dataset, { fetch });

  return loadHomeAssistantConnection(webId, fetch, normalized.id);
}

export async function deleteHomeAssistantConnection(
  webId,
  fetch,
  id = HOME_ASSISTANT_LEGACY_ID
) {
  if (!webId || !fetch) return;

  const normalizedId = normalizeHomeAssistantConnectionId(id);
  const documentUrls = [getHomeAssistantConnectionDocumentUrl(webId, normalizedId)];

  for (const documentUrl of Array.from(new Set(documentUrls))) {
    try {
      await deleteFile(documentUrl, { fetch });
    } catch (err) {
      const status = err?.statusCode || err?.response?.status;
      if (status !== 404) {
        throw err;
      }
    }
  }
}

const customConnectionFromThing = (thing, webId, documentUrl) => {
  if (!thing) return null;
  const types = getUrlAll(thing, RDF_TYPE);
  if (!types.includes(SDM_CUSTOM_CONNECTION_TYPE)) return null;

  const id =
    getStringNoLocale(thing, SDM_CONNECTION_ID) ||
    documentUrl.split("/").pop()?.replace(/^custom-/, "").replace(/\.ttl$/, "") ||
    "";
  if (!id) return null;

  const encryptedAuthSecret =
    getStringNoLocale(thing, SDM_CONNECTION_AUTH_SECRET) || "";
  const authType = normalizeAuthType(
    getStringNoLocale(thing, SDM_CONNECTION_AUTH_TYPE) || "none"
  );
  const url = getUrl(thing, SDM_CONNECTION_URL) || "";
  const endpoint = getUrl(thing, SDM_CONNECTION_ENDPOINT) || url;
  const port =
    getStringNoLocale(thing, SDM_CONNECTION_PORT) ||
    inferPortFromUrl(endpoint || url);

  return {
    id,
    kind: "custom",
    name: getStringNoLocale(thing, SDM_CONNECTION_NAME) || "Custom connection",
    description: getStringNoLocale(thing, SDM_CONNECTION_DESCRIPTION) || "",
    url,
    endpoint,
    port,
    sourceEndpoint: normalizeSourceEndpointPath(endpoint || url),
    storageDocumentUrl:
      getUrl(thing, SDM_CONNECTION_STORAGE_DOCUMENT) ||
      normalizeCustomStorageDocumentUrl(webId, "", id),
    method: normalizeHttpMethod(
      getStringNoLocale(thing, SDM_CONNECTION_METHOD) || "GET"
    ),
    authType,
    authSecret:
      encryptedAuthSecret && authType !== "none" && !encryptedAuthSecret.startsWith(SEALED_SECRET_PREFIX)
        ? decryptCredentialSecret(
            encryptedAuthSecret,
            webId,
            `${CUSTOM_SECRET_PREFIX}-${id}`
          )
        : "",
    encryptedAuthSecret,
    authHeader: getStringNoLocale(thing, SDM_CONNECTION_AUTH_HEADER) || "",
    headersJson: getStringNoLocale(thing, SDM_CONNECTION_HEADERS) || "",
    options: JSON.parse(getStringNoLocale(thing, `${SDM_NS}connectionOptionsJson`) || "{}"),
    documentUrl,
    createdAt: getDatetime(thing, DCTERMS_CREATED),
    modifiedAt: getDatetime(thing, DCTERMS_MODIFIED),
  };
};

export async function loadCustomConnection(webId, fetch, id) {
  const normalizedId = slugifyConnectionId(id);
  const documentUrl = getCustomConnectionDocumentUrl(webId, normalizedId);
  const thingUrl = getCustomConnectionThingUrl(webId, normalizedId);
  if (!documentUrl || !thingUrl || !fetch) return null;

  try {
    const dataset = await getSolidDataset(documentUrl, { fetch });
    const thing = getThing(dataset, thingUrl) || getThingAll(dataset)[0];
    return customConnectionFromThing(thing, webId, documentUrl);
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function loadCustomConnections(webId, fetch) {
  const containerUrl = getConnectionsContainerUrl(webId);
  if (!containerUrl || !fetch) return [];

  let containerDataset;
  try {
    containerDataset = await getSolidDataset(containerUrl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) return [];
    throw err;
  }

  const resourceUrls = getContainedResourceUrlAll(containerDataset).filter(
    (url) => !url.endsWith("/") && /\/custom-[^/]+\.ttl$/.test(url)
  );
  const connections = [];

  for (const resourceUrl of resourceUrls) {
    try {
      const dataset = await getSolidDataset(resourceUrl, { fetch });
      const thing = getThing(dataset, `${resourceUrl}#it`) || getThingAll(dataset)[0];
      const connection = customConnectionFromThing(thing, webId, resourceUrl);
      if (connection) connections.push(connection);
    } catch {
      // Ignore malformed custom connection documents.
    }
  }

  return connections.sort((a, b) => a.name.localeCompare(b.name));
}

export async function saveCustomConnection(webId, fetch, values) {
  if (!webId || !fetch) return null;

  const normalized = normalizeCustomConnection({ ...values, webId });
  await ensureConnectionsContainer(webId, fetch);

  const documentUrl = getCustomConnectionDocumentUrl(webId, normalized.id);
  const thingUrl = getCustomConnectionThingUrl(webId, normalized.id);
  let dataset;

  try {
    dataset = await getSolidDataset(documentUrl, { fetch });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) {
      dataset = createSolidDataset();
    } else {
      throw err;
    }
  }

  let connectionThing = getThing(dataset, thingUrl) || createThing({ url: thingUrl });
  connectionThing = removeAll(connectionThing, RDF_TYPE);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_ID);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_NAME);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_DESCRIPTION);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_URL);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_PORT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_ENDPOINT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_STORAGE_DOCUMENT);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_METHOD);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_AUTH_TYPE);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_AUTH_SECRET);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_AUTH_HEADER);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_HEADERS);
  connectionThing = removeAll(connectionThing, SDM_CONNECTION_TOKEN_ALGORITHM);
  connectionThing = removeAll(connectionThing, DCTERMS_MODIFIED);

  connectionThing = addUrl(connectionThing, RDF_TYPE, SDM_DATA_CONNECTION_TYPE);
  connectionThing = addUrl(connectionThing, RDF_TYPE, SDM_CUSTOM_CONNECTION_TYPE);
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_ID,
    normalized.id
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_NAME,
    normalized.name
  );
  if (normalized.description) {
    connectionThing = setStringNoLocale(
      connectionThing,
      SDM_CONNECTION_DESCRIPTION,
      normalized.description
    );
  }
  connectionThing = setUrl(connectionThing, SDM_CONNECTION_URL, normalized.url);
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_PORT,
    normalized.port
  );
  connectionThing = setUrl(
    connectionThing,
    SDM_CONNECTION_ENDPOINT,
    normalized.endpoint
  );
  connectionThing = setUrl(
    connectionThing,
    SDM_CONNECTION_STORAGE_DOCUMENT,
    normalized.storageDocumentUrl
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_METHOD,
    normalized.method
  );
  connectionThing = setStringNoLocale(
    connectionThing,
    SDM_CONNECTION_AUTH_TYPE,
    normalized.authType
  );
  if (normalized.authType !== "none") {
    connectionThing = setStringNoLocale(
      connectionThing,
      SDM_CONNECTION_AUTH_SECRET,
      await sealConnectionCredential(webId, fetch, `${CUSTOM_SECRET_PREFIX}-${normalized.id}`, normalized.authSecret, normalized.encryptedAuthSecret)
    );
    connectionThing = setStringNoLocale(
      connectionThing,
      SDM_CONNECTION_TOKEN_ALGORITHM,
      "worker-aes-gcm-v1"
    );
  }
  if (normalized.authHeader) {
    connectionThing = setStringNoLocale(
      connectionThing,
      SDM_CONNECTION_AUTH_HEADER,
      normalized.authHeader
    );
  }
  if (normalized.headersJson) {
    connectionThing = setStringNoLocale(
      connectionThing,
      SDM_CONNECTION_HEADERS,
      normalized.headersJson
    );
  }

  if (!getDatetime(connectionThing, DCTERMS_CREATED)) {
    connectionThing = setDatetime(connectionThing, DCTERMS_CREATED, new Date());
  }
  connectionThing = setDatetime(connectionThing, DCTERMS_MODIFIED, new Date());

  connectionThing = setStringNoLocale(connectionThing, `${SDM_NS}connectionOptionsJson`, JSON.stringify(normalized.options || {}));
  dataset = setThing(dataset, connectionThing);
  await saveSolidDatasetAt(documentUrl, dataset, { fetch });

  return loadCustomConnection(webId, fetch, normalized.id);
}

export async function deleteCustomConnection(webId, fetch, id) {
  const normalizedId = slugifyConnectionId(id);
  const documentUrl = getCustomConnectionDocumentUrl(webId, normalizedId);
  if (!documentUrl || !fetch) return;
  const documentUrls = [documentUrl];

  for (const targetUrl of Array.from(new Set(documentUrls))) {
    try {
      await deleteFile(targetUrl, { fetch });
    } catch (err) {
      const status = err?.statusCode || err?.response?.status;
      if (status !== 404) {
        throw err;
      }
    }
  }
}

async function loadSolidSyncJsonDocument(documentUrl, fetch) {
  const response = await fetch(documentUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      body
        ? `Solid Sync JSON could not be loaded (${response.status}): ${body}`
        : `Solid Sync JSON could not be loaded (${response.status}).`
    );
  }

  const body = await response.text();
  if (!body.trim()) return null;

  try {
    return JSON.parse(body);
  } catch {
    throw new Error("Existing Solid Sync resource is not valid JSON.");
  }
}

async function putSolidSyncJsonDocument(documentUrl, fetch, payload) {
  const response = await fetch(documentUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload, null, 2),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      body
        ? `Solid Sync JSON could not be saved (${response.status}): ${body}`
        : `Solid Sync JSON could not be saved (${response.status}).`
    );
  }
}

async function saveHomeAssistantSolidSyncJson(
  webId,
  fetch,
  documentUrl,
  entities,
  observedAt
) {
  const existing = await loadSolidSyncJsonDocument(documentUrl, fetch);
  const entries = extractSolidSyncEntries(existing);
  const usedKeys = new Set();
  const measurements = {};

  entities.forEach((entity) => {
    const key = getUniqueMeasurementKey(entity.entityId, usedKeys);
    measurements[key] = {
      entity_id: entity.entityId,
      state: entity.state ?? "",
      attributes: entity.attributes || {},
      last_changed: entity.lastChanged || null,
      last_updated: entity.lastUpdated || null,
    };
  });

  if (Object.keys(measurements).length > 0) {
    entries.push({
      captured_at: observedAt.toISOString(),
      measurements,
    });
  }

  const payload = {
    profile: "Home Assistant Entities",
    resource_path: getResourcePathForDocument(webId, documentUrl),
    updated_at: observedAt.toISOString(),
    entries,
  };

  await putSolidSyncJsonDocument(documentUrl, fetch, payload);

  return {
    documentUrl,
    count: entities.length,
    observedAt,
    dataModel: SOLID_SYNC_JSON_DATA_MODEL,
  };
}

export async function saveHomeAssistantEntitySnapshots(
  webId,
  fetch,
  entities = [],
  { storageDocumentUrl, dataModel } = {}
) {
  if (!webId || !fetch) return null;

  const cleanedEntities = entities.filter((entity) => entity?.entityId);
  const selectedDataModel = normalizeDataModel(dataModel);
  const defaultStorageDocumentUrl =
    selectedDataModel === SOLID_SYNC_JSON_DATA_MODEL
      ? getHomeAssistantSolidSyncDocumentUrl(webId)
      : "";
  const documentUrl = normalizeStorageDocumentUrl(
    webId,
    storageDocumentUrl || defaultStorageDocumentUrl,
    { allowRdf: selectedDataModel !== SOLID_SYNC_JSON_DATA_MODEL }
  );
  await ensureContainerAtUrl(getContainerUrlForDocument(documentUrl), fetch);

  const collectionThingUrl =
    getHomeAssistantEntityCollectionThingUrlFromDocument(documentUrl);
  const observedAt = new Date();

  if (selectedDataModel === SOLID_SYNC_JSON_DATA_MODEL) {
    return saveHomeAssistantSolidSyncJson(
      webId,
      fetch,
      documentUrl,
      cleanedEntities,
      observedAt
    );
  }

  let dataset;
  try {
    dataset = await getSolidDataset(documentUrl, { fetch });
    getThingAll(dataset)
      .filter((thing) => {
        const types = getUrlAll(thing, RDF_TYPE);
        return (
          thing.url === collectionThingUrl ||
          types.includes(SDM_HOME_ASSISTANT_ENTITY_TYPE) ||
          (types.includes(SOSA_OBSERVATION) &&
            Boolean(getStringNoLocale(thing, SDM_ENTITY_ID))) ||
          (types.includes(SOSA_OBSERVABLE_PROPERTY) &&
            Boolean(getStringNoLocale(thing, SDM_ENTITY_ID)))
        );
      })
      .forEach((thing) => {
        dataset = removeThing(dataset, thing);
      });
  } catch (err) {
    const status = err?.statusCode || err?.response?.status;
    if (status === 404) {
      dataset = createSolidDataset();
    } else {
      throw err;
    }
  }

  let collectionThing = createThing({ url: collectionThingUrl });
  collectionThing = addUrl(
    collectionThing,
    RDF_TYPE,
    SDM_HOME_ASSISTANT_ENTITY_COLLECTION_TYPE
  );
  collectionThing = setStringNoLocale(
    collectionThing,
    SDM_CONNECTION_NAME,
    "Home Assistant Entities"
  );
  collectionThing = setStringNoLocale(
    collectionThing,
    SDM_CONNECTION_DATA_MODEL,
    selectedDataModel
  );
  collectionThing = setDatetime(collectionThing, DCTERMS_MODIFIED, observedAt);
  dataset = setThing(dataset, collectionThing);

  cleanedEntities.forEach((entity) => {
    const thingUrl = getHomeAssistantEntityThingUrl(documentUrl, entity.entityId);
    if (!thingUrl) return;

    let entityThing = createThing({ url: thingUrl });
    entityThing = addUrl(
      entityThing,
      RDF_TYPE,
      selectedDataModel === "sosa-observation"
        ? SOSA_OBSERVATION
        : SDM_HOME_ASSISTANT_ENTITY_TYPE
    );
    entityThing = setStringNoLocale(entityThing, SDM_ENTITY_ID, entity.entityId);
    entityThing = setStringNoLocale(
      entityThing,
      SDM_ENTITY_DOMAIN,
      entity.domain || ""
    );
    entityThing = setStringNoLocale(
      entityThing,
      SDM_ENTITY_NAME,
      entity.friendlyName || entity.entityId
    );
    entityThing = setStringNoLocale(
      entityThing,
      SDM_ENTITY_STATE,
      String(entity.state ?? "")
    );
    if (entity.unit) {
      entityThing = setStringNoLocale(entityThing, SDM_ENTITY_UNIT, entity.unit);
    }
    if (entity.deviceClass) {
      entityThing = setStringNoLocale(
        entityThing,
        SDM_ENTITY_DEVICE_CLASS,
        entity.deviceClass
      );
    }
    entityThing = setStringNoLocale(
      entityThing,
      SDM_ENTITY_ATTRIBUTES,
      stringifyAttributes(entity.attributes)
    );
    entityThing = setDatetime(entityThing, SDM_OBSERVED_AT, observedAt);

    if (selectedDataModel === "sosa-observation") {
      const propertyThingUrl = getHomeAssistantObservablePropertyThingUrl(
        documentUrl,
        entity.entityId
      );
      if (propertyThingUrl) {
        let propertyThing = createThing({ url: propertyThingUrl });
        propertyThing = addUrl(propertyThing, RDF_TYPE, SOSA_OBSERVABLE_PROPERTY);
        propertyThing = setStringNoLocale(
          propertyThing,
          SDM_ENTITY_ID,
          entity.entityId
        );
        propertyThing = setStringNoLocale(
          propertyThing,
          RDFS_LABEL,
          entity.friendlyName || entity.entityId
        );
        if (entity.unit) {
          propertyThing = setStringNoLocale(
            propertyThing,
            SDM_ENTITY_UNIT,
            entity.unit
          );
        }
        dataset = setThing(dataset, propertyThing);
        entityThing = setUrl(
          entityThing,
          SOSA_OBSERVED_PROPERTY,
          propertyThingUrl
        );
      }
      entityThing = setStringNoLocale(
        entityThing,
        SOSA_HAS_SIMPLE_RESULT,
        String(entity.state ?? "")
      );
      entityThing = setDatetime(entityThing, SOSA_RESULT_TIME, observedAt);
    }

    entityThing = setOptionalDatetime(
      entityThing,
      SDM_LAST_CHANGED,
      entity.lastChanged
    );
    entityThing = setOptionalDatetime(
      entityThing,
      SDM_LAST_UPDATED,
      entity.lastUpdated
    );

    dataset = setThing(dataset, entityThing);
  });

  await saveSolidDatasetAt(documentUrl, dataset, { fetch });

  return {
    documentUrl,
    count: cleanedEntities.length,
    observedAt,
    dataModel: selectedDataModel,
  };
}
